import Accordion from "@/common/components/Accordion";
import { BaseSyntheticEvent } from "react";
import { SubmitHandler, useFormContext } from "react-hook-form";
import { EstablishmentLeadsFunnelUpdateValidationSchema } from "../../schemas/update.schema";
import { EstablishmentLeadData, MaintenanceSpecificationDirectiveKlass } from "../../types/establishmentLead.type";
import { useUpdateEstablishmentLeadMaintenanceModels } from "../../hooks/maintenanceModules/useUpdateEstablishmentLeadMaintenanceModels";
import { FormTransformers } from "@/common/utils/formTransformers";
import { useQueryClient } from "@tanstack/react-query";
import MaintenanceGeneralInformationSection from "./MaintenanceGeneralInformationSection";
import MaintenanceSpecificationsSection from "./maintenanceSpecification/MaintenanceSpecificationsSection";

interface MaintenanceInformationSectionProps {
  establishmentLead: EstablishmentLeadData;
  submitLead?: (e?: BaseSyntheticEvent) => Promise<void>;
  disabled?: boolean;
  allowUpdateSpecifications?: boolean;
}

const MaintenanceInformationSection = ({ establishmentLead, submitLead, disabled, allowUpdateSpecifications }: MaintenanceInformationSectionProps) => {
  const queryClient = useQueryClient();

  const {
    handleSubmit,
    setError,
    watch,
  } = useFormContext<EstablishmentLeadsFunnelUpdateValidationSchema>();

  const {
    mutate: updateEstablishmentLeadMaintenanceModels,
    isPending: updateEstablishmentLeadMaintenanceModelsIsPending,
  } = useUpdateEstablishmentLeadMaintenanceModels({
    id: establishmentLead.id,
    establishmentLead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['establishmentLead', establishmentLead.id] });
    },
    onError: FormTransformers.errorsTransformer<EstablishmentLeadsFunnelUpdateValidationSchema>({
      setError
    }),
  });

  const onSubmit: SubmitHandler<EstablishmentLeadsFunnelUpdateValidationSchema> = (data) => updateEstablishmentLeadMaintenanceModels(data);

  const submitMaintenanceModels = handleSubmit(onSubmit);

  const disabledFields = disabled ||
    updateEstablishmentLeadMaintenanceModelsIsPending

  return (
    <>
      {watch('cm_module_enabled') && (
        <Accordion
          defaultOpen
          head="Dados para manutenção (CM)"
          className="p-4"
          triggerClassName="px-0 py-1 text-[18px] font-bold"
          bodyClassName="pt-4 pb-0"
          body={(
            <div className="flex flex-col gap-4">
              <Accordion
                defaultOpen
                head="Diagnóstico e Orçamento"
                className="p-4"
                triggerClassName="px-0 py-1 text-[18px] font-bold"
                bodyClassName="pt-4 pb-0"
                body={(
                  <MaintenanceSpecificationsSection
                    establishmentLead={establishmentLead}
                    directiveKlass={MaintenanceSpecificationDirectiveKlass.DiagnosticEntry}
                    alertText={{
                      title: 'Tudo que for especificado será homologado para Diagnostico e Orçamento.',
                      description: 'Não adicione o que não é capaz de atender.',
                    }}
                    allowUpdate={allowUpdateSpecifications}
                  />
                )}
              />

              <Accordion
                defaultOpen
                head="Garantia de fábrica (Concessionária)"
                className="p-4"
                triggerClassName="px-0 py-1 text-[18px] font-bold"
                bodyClassName="pt-4 pb-0"
                body={(
                  <MaintenanceSpecificationsSection
                    establishmentLead={establishmentLead}
                    directiveKlass={MaintenanceSpecificationDirectiveKlass.ScrapEntry}
                    alertText={{
                      title: 'Tudo que for especificado será homologado para atendimento nos serviços de Garantia de fábrica.',
                      description: 'Não adicione o que não é capaz de atender.',
                    }}
                    allowUpdate={allowUpdateSpecifications}
                  />
                )}
              />

              <MaintenanceGeneralInformationSection
                establishmentLead={establishmentLead}
                submitLead={submitLead}
                submitMaintenanceModels={submitMaintenanceModels}
                disabled={disabledFields}
              />

              <MaintenanceSpecificationsSection
                establishmentLead={establishmentLead}
                directiveKlass={MaintenanceSpecificationDirectiveKlass.ExecutionEntry}
                alertText={{
                  title: 'Tudo que for especificado será homologado para atendimento no CM.',
                  description: 'Não adicione o que não é capaz de atender.',
                }}
                allowUpdate={allowUpdateSpecifications}
              />
            </div>
          )}
        />
      )}
    </>
  );
}

export default MaintenanceInformationSection;
